import { FaqSchema } from "@/components/FaqSchema";
import { useState, useEffect } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { BottomNavigation } from "@/components/BottomNavigation";
import { useClubContext } from "@/hooks/use-club-context";
import { supabase } from "@/integrations/supabase/client";
import husselAppLogo from "@/assets/hussel-app-logo.png";

const HowItWorks = () => {
  const navigate = useNavigate();
  const { currentClub } = useClubContext();
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from('profiles').select('club_id').eq('user_id', user.id).single();
      const today = new Date().toISOString().split('T')[0];
      let query = supabase.from('sessions').select('id, status, end_time, session_date').eq('session_date', today).eq('status', 'active');
      if (profile?.club_id) query = query.eq('club_id', profile.club_id);
      const { data: session } = await query.maybeSingle();
      if (session) {
        const now = new Date();
        const endParts = session.end_time.split(':');
        const sessionEnd = new Date(session.session_date);
        sessionEnd.setHours(parseInt(endParts[0]), parseInt(endParts[1]), 0, 0);
        setHasActiveSession(now <= sessionEnd);
      }
    };
    checkSession();
  }, []);
  const faqs = [
    {
      question: "Hoe werkt de Hussel App?",
      answer:
        "De Hussel App verdeelt automatisch en slim spelers over de beschikbare banen tijdens hussel- of tossavonden. Het algoritme kijkt naar speelsterkte (niveau 1-9), het aantal gespeelde rondes, tegen wie je al hebt gespeeld, en rustperiodes. Zo krijg je elke ronde een eerlijke en gevarieerde indeling — zonder dat iemand het handmatig hoeft te doen.",
    },
    {
      question: "Hoe installeer ik de app?",
      answer: (
        <>
          <strong>📱 iPhone / iPad (Safari):</strong>{"\n"}
          1. Open de app in <strong>Safari</strong>{"\n"}
          2. Tik op het <strong>deel-icoon</strong>{' '}
          <span className="inline-flex items-center justify-center w-6 h-6 border border-muted-foreground rounded align-middle mx-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </span>{' '}
          (onderaan het scherm){"\n"}
          3. Kies <strong>'Zet op beginscherm'</strong>{"\n"}
          <span className="text-xs text-muted-foreground ml-4">💡 Staat deze optie er niet bij? Scroll naar beneden of tik op <strong>'Toon meer'</strong> (^).</span>{"\n"}
          4. Tik op <strong>'Voeg toe'</strong>{"\n\n"}
          <strong>🤖 Android (Chrome):</strong>{"\n"}
          1. Open de app in <strong>Chrome</strong>{"\n"}
          2. Tik op het <strong>menu-icoon</strong>{' '}
          <span className="inline-flex items-center justify-center w-6 h-6 border border-muted-foreground rounded align-middle mx-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
          </span>{' '}
          (drie puntjes, rechtsboven){"\n"}
          3. Kies <strong>'App installeren'</strong> of <strong>'Toevoegen aan startscherm'</strong>{"\n"}
          4. Bevestig met <strong>'Installeren'</strong>{"\n\n"}
          De app verschijnt als icoon op je startscherm en werkt net als een echte app! 🎾{"\n\n"}
          <span className="text-xs text-muted-foreground">
            Meer informatie? Bekijk het antwoord bij{' '}
            <button
              className="text-primary underline"
              onClick={() => {
                const el = document.querySelector('[data-value="item-10"]');
                if (el) {
                  const trigger = el.querySelector('[data-radix-collection-item]') as HTMLButtonElement;
                  if (trigger && trigger.getAttribute('data-state') === 'closed') trigger.click();
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
            >
              'Ik kan de app niet vinden in de App Store'
            </button>.
          </span>
        </>
      ),
    },
    {
      question: "Hoe meld ik me aan / af?",
      answer:
        "Als je inlogt op het hoofdscherm, of in het menu op 'aan/afmelden' kan je jezelf aanmelden. Dit kan vanaf 30 minuten voorafgaand aan de sessie. Je meldt je dan aan voor het eerstvolgende ronde. Net zoals je je pasje neerlegt op het husselbord. Je kan eventueel (niet verplicht) aangeven tot welke eindtijd je wilt blijven. Je kan dit later nog wijzigen. Als je dit hebt gedaan wordt je aanmelding automatisch ongedaan gemaakt. Wil je zelf bepalen wanneer je stopt? Klik dan op 'Pasje omdraaien' bij aan/afmelden. Je wordt dan de eerstvolgende ronde niet meer ingedeeld. Wil je daarna toch weer spelen? Dan meld je je opnieuw aan.",
    },
    {
      question: "Hoe werkt de indeling/hussel?",
      answer:
        "Het systeem maakt een slimme indeling op basis van meerdere variabelen:\n\n• Speelsterkte: je wordt ingedeeld met spelers die maximaal 2 niveaus van jou verschillen.\n• Gespeelde rondes: wie minder heeft gespeeld krijgt voorrang.\n• Eerdere tegenstanders: het systeem vermijdt dat je steeds met dezelfde spelers speelt (cooling period).\n• Rustperiodes: na meerdere rondes achter elkaar word je automatisch een ronde 'gerust'.\n• Beschikbaarheid: als je een eindtijd hebt opgegeven word je niet meer ingedeeld als die tijd is verstreken.\n\nZo ontstaat elke ronde een eerlijke, gevarieerde en leuke indeling.",
    },
    {
      question: "Wat als ik niet word ingedeeld?",
      answer:
        "Als er meer spelers zijn dan beschikbare plekken, kan het zijn dat je een ronde moet wachten. Het systeem zorgt ervoor dat je maximaal 1 ronde achter elkaar hoeft te wachten. Spelers die al hebben gewacht krijgen automatisch voorrang bij de volgende indeling.",
    },
    {
      question: "Wat betekent 'gespeeld'?",
      answer:
        "Het aantal 'gespeeld' geeft aan hoe vaak je daadwerkelijk bent ingedeeld op een baan tijdens de huidige sessie. Dit telt alleen mee als je ook echt op een baan bent geplaatst.",
    },
    {
      question: "Kan ik aangeven hoelang ik wil spelen?",
      answer:
        "Ja! Bij het aanmelden kun je een eindtijd opgeven. Het systeem houdt hier rekening mee en deelt je niet meer in nadat je limiet is bereikt.",
    },
    {
      question: "Hoe kan ik stoppen met spelen?",
      answer:
        "Je kunt op elk moment je 'pasje omdraaien' in de app. Hiermee geef je aan dat je niet meer wilt worden ingedeeld voor de rest van de avond. Je kunt dit later weer ongedaan maken als je toch weer wilt spelen.",
    },
    {
      question: "Waarom word ik soms met dezelfde mensen ingedeeld?",
      answer:
        "Het systeem probeert je steeds met andere spelers in te delen, maar dit is niet altijd mogelijk. Als er weinig spelers zijn met een vergelijkbaar niveau, kan het voorkomen dat je vaker met dezelfde mensen speelt. Het belangrijkste doel is dat iedereen zo veel mogelijk kan tennissen.",
    },
    {
      question: "Hoe werken de meldingen?",
      answer:
        "Als je meldingen hebt ingeschakeld, ontvang je een bericht wanneer er een nieuwe ronde-indeling is gemaakt. Zo weet je meteen op welke baan je moet zijn. Je kunt meldingen aan- en uitzetten via het bel-icoontje.",
    },
    {
      question: "Ik kan de app niet vinden in de Apple Store (iOS) of Play Store (Android)",
      answer: (
        <>
          Dat klopt helemaal! Daar zijn we nog mee bezig. Maar wist je dat dat eigenlijk helemaal niet nodig is? Je kan de app namelijk via de website direct op je telefoon installeren.{"\n\n"}
          Het is niet echt een 'app' maar een website die werkt als een app, maar dan zonder de browser-elementen. Eigenlijk installeer je een snelkoppeling. Er zitten wel extra functies bij, zoals de mogelijkheid meldingen te ontvangen — wel zo handig bij een nieuwe speelronde!{"\n\n"}
          <strong>📱 iPhone / iPad (Safari):</strong>{"\n"}
          1. Open de app in <strong>Safari</strong>{"\n"}
          2. Tik op het <strong>deel-icoon</strong> <span className="inline-flex items-center justify-center w-6 h-6 border border-muted-foreground rounded align-middle mx-1"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> (vierkantje met pijl omhoog, onderaan het scherm){"\n"}
          3. Scroll naar beneden en kies <strong>'Zet op beginscherm'</strong>{"\n"}
          <span className="text-xs text-muted-foreground ml-4">💡 Staat deze optie er niet bij? Scroll naar beneden of tik op <strong>'Toon meer'</strong> (^).</span>{"\n"}
          4. Tik op <strong>'Voeg toe'</strong>{"\n\n"}
          <strong>🤖 Android (Chrome):</strong>{"\n"}
          1. Open de app in <strong>Chrome</strong>{"\n"}
          2. Tik op het <strong>menu-icoon</strong> <span className="inline-flex items-center justify-center w-6 h-6 border border-muted-foreground rounded align-middle mx-1"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg></span> (drie puntjes, rechtsboven){"\n"}
          3. Kies <strong>'App installeren'</strong> of <strong>'Toevoegen aan startscherm'</strong>{"\n"}
          4. Bevestig met <strong>'Installeren'</strong>{"\n\n"}
          De app verschijnt als icoon op je startscherm en werkt net als een echte app! 🎾
        </>
      ),
    },
    {
      question: "Fout ontdekt? Nieuwe functie?",
      answer: (
        <>
          Werkt iets niet correct of wil je een nieuwe functie laten toevoegen? Stuur de ontwikkelaar een bericht: via
          email:{" "}
          <a href="mailto:info@ayemate.nl" target="_blank" rel="noopener noreferrer" className="text-primary underline">
            info@ayemate.nl
          </a>
        </>
      ),
    },
  ];

  const handleViewChange = (view: string) => {
    // All other views are on the dashboard, so navigate there
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen p-6 pb-28 relative">
      <div className="max-w-4xl mx-auto space-y-6 relative z-0">
        <AppHeader
          clubLogo={currentClub?.logo_url}
          clubName={currentClub?.name ? `${currentClub.name} Hussel App` : "De Hussel App"}
        />

        <Card className="p-6 bg-card relative z-0">
          <h2 className="text-2xl font-bold mb-6">Hoe werkt het?</h2>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} data-value={`item-${index}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground whitespace-pre-line">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </div>

      <FaqSchema
        faqs={faqs
          .filter((f): f is { question: string; answer: string } => typeof f.answer === "string")}
      />
      <Footer />
      <BottomNavigation role="player" activeView="howItWorks" onViewChange={handleViewChange} hasActiveSession={hasActiveSession} />
    </div>
  );
};

export default HowItWorks;
